import { useEffect } from "react";
const kMaxRafAttempts = 120; // ~2 seconds at 60fps
/**
 * Returns the chip's bbox in coordinates relative to its `.text-tool-wrapper`, in
 * layout (pre-transform) pixels — the coordinate system the annotation layer combines
 * with tile offsets. `getBoundingClientRect` returns viewport pixels, so we divide the
 * wrapper-relative delta by the wrapper's effective CSS scale to get layout coords.
 *
 * @param chipEl - The chip's DOM element.
 * @param shrinkBy - Pixels to subtract from width/height (a small visual inset so the
 *                   sparrow doesn't touch the chip's edge directly).
 * @returns The bbox, or `undefined` if the chip or wrapper hasn't been laid out.
 */
export function getChipBoxInWrapperCoords(chipEl, shrinkBy) {
    const wrapperEl = chipEl.closest(".text-tool-wrapper");
    if (!wrapperEl || !wrapperEl.offsetWidth)
        return undefined;
    const chipRect = chipEl.getBoundingClientRect();
    if (chipRect.width <= 0 || chipRect.height <= 0)
        return undefined;
    const wrapperRect = wrapperEl.getBoundingClientRect();
    const scale = wrapperRect.width / wrapperEl.offsetWidth;
    if (!Number.isFinite(scale) || scale <= 0)
        return undefined;
    return {
        left: (chipRect.left - wrapperRect.left) / scale,
        top: (chipRect.top - wrapperRect.top) / scale,
        width: Math.max(0, chipRect.width - shrinkBy) / scale,
        height: Math.max(0, chipRect.height - shrinkBy) / scale
    };
}
/**
 * Wires up chip-measurement: initial measure, rAF retry until the chip has non-zero
 * dimensions (covers remounts into containers whose layout settles after first paint,
 * like 4-up cells and scaled thumbnails), and a `ResizeObserver` on the chip and its
 * `.text-tool-wrapper` ancestor (the wrapper catches container-driven reflows that
 * don't resize the chip's own inline box).
 *
 * Takes the chip element directly rather than a ref so the effect re-runs when the
 * element appears or changes — refs are stable across renders even when `.current`
 * updates, so a conditionally-rendered chip wouldn't otherwise be measured. Callers
 * drive this with a `useState` + callback ref pattern.
 *
 * @param chipEl - The chip's DOM element, or `null` while it isn't mounted.
 * @param measure - Caller's bbox-publishing callback; responsible for its own zero-
 *                  size guard.
 * @param trigger - Optional extra dependency that forces a re-run when its value
 *                  changes (used by highlights to re-measure on Slate revision changes).
 */
export function useChipMeasurement(chipEl, measure, trigger) {
    useEffect(() => {
        if (!chipEl)
            return;
        const wrapperEl = chipEl.closest('.text-tool-wrapper');
        measure();
        let rafId = 0;
        let attempts = 0;
        const retryIfZero = () => {
            const rect = chipEl.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0)
                return;
            attempts++;
            if (attempts >= kMaxRafAttempts)
                return;
            rafId = requestAnimationFrame(() => {
                measure();
                retryIfZero();
            });
        };
        retryIfZero();
        const resizeObs = new ResizeObserver(measure);
        resizeObs.observe(chipEl);
        if (wrapperEl)
            resizeObs.observe(wrapperEl);
        return () => {
            if (rafId)
                cancelAnimationFrame(rafId);
            resizeObs.disconnect();
        };
    }, [chipEl, measure, trigger]);
}
//# sourceMappingURL=data:application/json;base64,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